/**
 * Sprint 138 — Cómo se dice lo que lleva dentro un paquete.
 *
 * Vive en `shared` y no en un servicio porque lo dicen **cuatro voces distintas** sobre el mismo
 * hecho: la térmica, la comanda del navegador, el PDF y la previsualización en pantalla. Cuando
 * cada una lo escribía a su manera, tres de ellas terminaron imprimiendo el paquete a secas
 * durante decenas de sprints y nadie lo notó hasta que lo dijo un mesero.
 *
 * Aquí no hay consulta ni formato: sólo el vocabulario.
 */
import { decirCantidad, esUnidadContinua } from './medidas';

/** Un componente ya resuelto a lo que se va a servir, listo para rotular. */
export interface ComponenteRotulable {
  /** Cuánto, dicho como en la cocina: "1 kg", "500 g", "2". Vacío cuando no aporta nada. */
  cantidad: string;
  nombre: string;
  /** El cliente cambió el producto que traía el paquete por otro. */
  esSustituto: boolean;
  /** El cliente pidió que este renglón NO vaya (Sprint 44). */
  excluido: boolean;
}

/**
 * Un componente del paquete, en una línea de papel angosto.
 *
 * Tres marcas y ninguna es decorativa: `+` es lo que va, `- SIN` es lo que el cliente quitó, y
 * `(cambio)` avisa que **no** es el producto que el paquete trae de fábrica. Sin esa última, la
 * cocina prepara el default de memoria y el cliente recibe lo que no pidió.
 */
export function lineaComponente(c: ComponenteRotulable): string {
  if (c.excluido) return `- SIN ${c.nombre}`;
  const cantidad = c.cantidad ? `${c.cantidad} ` : '';
  return `+ ${cantidad}${c.nombre}${c.esSustituto ? ' (cambio)' : ''}`;
}

/**
 * Sprint 169 — Lo que trae el paquete, seguido y separado por comas, para el ticket del CLIENTE.
 *
 * Claudia: *"se desperdicia mucho papel"*. Un "Espadas Res" ocupaba diez renglones, uno por cada cosa
 * que trae, y el ticket de caja promediaba 16 cm. El cliente sólo necesita saber qué incluye: en un
 * párrafo cabe en tres o cuatro renglones. **La comanda de cocina NO cambia**: ahí cada cosa va en su
 * renglón porque se arma leyendo de pie, una por una.
 */
export function componentesEnRenglon(cs: readonly ComponenteRotulable[]): string {
  return cs.map((c) => (c.excluido ? `SIN ${c.nombre}` : lineaComponente(c).replace(/^\+ /, ''))).join(', ');
}

/**
 * La cantidad como se dice en la cocina.
 *
 * ## Sprint 154 — la comanda mandaba servir ocho veces de más
 *
 * La dueña, el 23/09: *"para corregir en gramos, me sale un kilo… al igual que los frijoles, sale
 * un litro"*. La comanda decía:
 *
 * ```
 * + 1 litro Frijoles Charros 1/2 litro
 * + 1 kg Lomo de Cerdo 125 gr
 * ```
 *
 * La cocina lee lo primero: **un kilo de lomo donde se vendieron 125 gramos**, ocho veces más.
 *
 * El error era de lectura del dato, no de formato. En un paquete, `cantidad` cuenta **cuántas
 * porciones** de ese producto lleva —y el producto, con el modelo de formatos, **ya trae su tamaño
 * en el nombre**: "Lomo de Cerdo 125 gr"—. La `unidad` que viaja al lado es la del producto base,
 * un vestigio del modelo viejo, y al imprimirla se leía como si fuera la magnitud servida.
 *
 * Comprobado sobre el catálogo real del cliente antes de cambiarlo: **19 renglones de sus paquetes
 * imprimían la unidad así, y ni uno solo usaba la cantidad como magnitud** (todas enteras, todos
 * los productos con su tamaño en el nombre).
 *
 * La regla, entonces:
 *
 *  - **Un entero cuenta porciones** → se dice el número a secas, y nada cuando es una: el nombre
 *    del producto ya dice qué se sirve. `+ Lomo de Cerdo 125 gr`, `+ 2 Pan de Ajo`.
 *  - **Un decimal es una magnitud** del modelo viejo —"0.25 kg de arrachera" sobre un producto sin
 *    tamaño en el nombre— y ahí la unidad **sí** hace falta. Ese caso no existe en este cliente,
 *    pero quitarle la unidad dejaría a otra cocina sin saber cuánto servir.
 */
export function decirCantidadComponente(cantidad: number, unidad: string): string {
  const u = (unidad ?? '').trim().toLowerCase();
  if (esUnidadContinua(u) && !Number.isInteger(cantidad)) return decirCantidad(cantidad, u);
  if (cantidad === 1) return '';
  return String(Math.round(cantidad * 1e6) / 1e6);
}
